import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { API_CONFIG } from '../config/api'
import { handleAccountSuspended } from '../utils/authErrorHandler'
import type {
    CmPartner,
    CmDeal,
    CmActivity,
    CmTsdCompany,
    CmTsdContact,
    CmPlaybook,
    CmContactGroup,
    CmTodaysActions,
    CmKpis,
    CmDiagnosticRow,
    CmSignal,
    CmProfile,
    CmSettings,
    CmPartnerTier,
    CmRelationshipStage,
    CmDealStage,
    CmLostReason,
    CmActivityType,
    CmMeetingType,
    CmMeetingNature,
    CmSentiment,
    CmStepStatus,
    CmPriority,
    CmShortlist,
    CmIppProfile,
    CmIppAttrs,
    CmBookCandidate,
    CmBookRow,
    CmBookFilter,
    CmYourBook,
    CmPartnerPeek,
    CmLogEntry,
    CmTouchKind
} from '../types/cm'

const cm = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout
})

cm.interceptors.response.use(
    (r) => r,
    (error) => {
        Sentry.addBreadcrumb({
            category: 'cm-api',
            message: `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network'}`,
            level: error.response?.status >= 500 ? 'error' : 'warning'
        })
        if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_SUSPENDED') {
            handleAccountSuspended()
        }
        return Promise.reject(error)
    }
)

function authHeaders(token: string) {
    return { headers: { Authorization: `Bearer ${token}` } }
}

// ============================================
// Onboarding (public — no token)
// ============================================

export interface SupplierSearchResult {
    id: string
    name: string
    slug: string
    logo_url: string | null
}

export async function searchSuppliersPublic(q: string): Promise<SupplierSearchResult[]> {
    if (!q || q.length < 2) return []
    const res = await cm.get('/api/cm-onboarding/supplier-search', { params: { q } })
    return res.data.suppliers
}

export interface CmSignupRequest {
    email: string
    password: string
    name: string
    title?: string
    supplierId?: string
    supplierNameRaw?: string
    territoryRegion?: 'NE' | 'SE' | 'MW' | 'SW' | 'W'
}

export async function cmSignup(data: CmSignupRequest) {
    const res = await cm.post('/api/cm-onboarding/signup', data)
    return res.data
}

export async function cmCompleteOnboarding(token: string) {
    const res = await cm.post('/api/cm-onboarding/complete', {}, authHeaders(token))
    return res.data
}

// ============================================
// Profile + settings
// ============================================

export async function getCmProfile(token: string): Promise<{ channelManager: CmProfile }> {
    const res = await cm.get('/api/cm/me', authHeaders(token))
    return res.data
}

export async function updateCmSettings(token: string, patch: Partial<CmSettings>): Promise<{ settings: CmSettings }> {
    const res = await cm.patch('/api/cm/settings', patch, authHeaders(token))
    return res.data
}

// ============================================
// Partners
// ============================================

export async function listCmPartners(
    token: string,
    filters: { tier?: CmPartnerTier; stage?: CmRelationshipStage; pulse?: string } = {}
): Promise<{ partners: CmPartner[] }> {
    const res = await cm.get('/api/cm/partners', { ...authHeaders(token), params: filters })
    return res.data
}

export async function getCmPartner(token: string, id: string): Promise<{ partner: CmPartner }> {
    const res = await cm.get(`/api/cm/partners/${id}`, authHeaders(token))
    return res.data
}

export interface CreateCmPartnerRequest {
    name: string
    company: string
    title?: string
    email?: string
    phone?: string
    linkedin?: string
    locationCity?: string
    locationState?: string
    tier: CmPartnerTier
    relationshipStage?: CmRelationshipStage
    source?: string
    mrr?: number
}

export async function createCmPartner(token: string, data: CreateCmPartnerRequest): Promise<{ partner: CmPartner }> {
    const res = await cm.post('/api/cm/partners', data, authHeaders(token))
    return res.data
}

export async function updateCmPartner(token: string, id: string, patch: Partial<CmPartner>): Promise<{ partner: CmPartner }> {
    const res = await cm.patch(`/api/cm/partners/${id}`, patch, authHeaders(token))
    return res.data
}

export async function deleteCmPartner(token: string, id: string) {
    const res = await cm.delete(`/api/cm/partners/${id}`, authHeaders(token))
    return res.data
}

export interface BulkImportResult {
    partners: number
    deals: number
    activities: number
    partnerIdMap: Record<string, string>
    dealIdMap: Record<string, string>
}

export async function bulkImportCm(token: string, data: { partners?: Array<Record<string, unknown>>; deals?: Array<Record<string, unknown>>; activities?: Array<Record<string, unknown>> }): Promise<BulkImportResult> {
    const res = await cm.post('/api/cm/import', data, authHeaders(token))
    return res.data
}

// ============================================
// Companion import (G1.2) + directory recognition
// ============================================

export type CmImportColumnType = 'PERSON' | 'FIRM' | 'OLD_TIER' | 'LAST_CONTACT' | 'PHONE' | 'EMAIL' | 'NOTES' | 'IGNORE'
export type CmImportFileKind = 'XLSX' | 'CSV'

export interface CmImportColumn {
    index: number
    letter: string
    header: string
    sample: string
    type: CmImportColumnType
    confidence: number
    note: string
}

export interface CmImportSummary {
    partners: number
    firms: number
    recognized: number
    noDateYet: number
    cantReach: number
    skipped: { inBook: number; inFile: number; noName: number }
    duplicates: Array<{ row: number; reason: 'IN_BOOK' | 'IN_FILE'; name: string; company: string }>
}

export interface CmImportPreview {
    fileName: string
    fileKind: CmImportFileKind
    headers: string[]
    rows: string[][]
    columns: CmImportColumn[]
    summary: CmImportSummary
}

export interface CmFirmMatch { id: string; kind: 'TSD' | 'YOUR_TSD'; firm: string; meta: string }
export interface CmApprovedMatch { firm: string; id: string; kind: CmFirmMatch['kind'] }

export interface CmImportCommitRequest {
    fileName: string
    fileKind: CmImportFileKind
    columns: Array<{ index: number; type: CmImportColumnType }>
    rows: string[][]
    matches?: CmApprovedMatch[]
    dryRun?: boolean
}

export interface CmImportCommitResult {
    summary: CmImportSummary
    created?: { sourceFileId: string; partnerIds: string[] }
}

export async function previewCmImport(token: string, file: File): Promise<CmImportPreview> {
    const form = new FormData()
    form.append('file', file)
    const res = await cm.post('/api/cm/import/preview', form, authHeaders(token))
    return res.data
}

export async function commitCmImport(token: string, data: CmImportCommitRequest): Promise<CmImportCommitResult> {
    const res = await cm.post('/api/cm/import/commit', data, authHeaders(token))
    return res.data
}

export async function undoCmImport(token: string, data: { sourceFileId: string; partnerIds: string[] }): Promise<{ removed: number }> {
    const res = await cm.post('/api/cm/import/undo', data, authHeaders(token))
    return res.data
}

export async function completeCmOnboarding(token: string): Promise<{ onboardedAt: string }> {
    const res = await cm.post('/api/cm/onboarding/complete', {}, authHeaders(token))
    return res.data
}

export async function recognizeCmFirms(token: string, names: string[]): Promise<{ results: Array<{ name: string; match: CmFirmMatch | null }> }> {
    const res = await cm.post('/api/cm/recognize', { names }, authHeaders(token))
    return res.data
}

// ============================================
// Build the book + Your book (G1.3)
// ============================================

export async function getCmShortlist(token: string): Promise<CmShortlist> {
    const res = await cm.get('/api/cm/book/shortlist', authHeaders(token))
    return res.data
}

/** Saves the profile and returns the re-ranked shortlist. */
export async function saveCmIppProfile(token: string, profile: CmIppProfile): Promise<CmShortlist> {
    const res = await cm.put('/api/cm/book/ipp-profile', profile, authHeaders(token))
    return res.data
}

export async function setCmPartnerIpp(token: string, id: string, attrs: Partial<CmIppAttrs>): Promise<{ candidate: CmBookCandidate }> {
    const res = await cm.patch(`/api/cm/book/partners/${id}/ipp`, attrs, authHeaders(token))
    return res.data
}

/** Replaces the depth book. More than 25 is a 409 (code DEPTH_CAP). */
export async function setCmDepthBook(token: string, partnerIds: string[]): Promise<{ depthCount: number; tiersApplied: number }> {
    const res = await cm.put('/api/cm/book/depth', { partnerIds }, authHeaders(token))
    return res.data
}

export async function setCmInDepthBook(token: string, id: string, inDepthBook: boolean): Promise<{ depthCount: number }> {
    const res = await cm.patch(`/api/cm/book/depth/${id}`, { inDepthBook }, authHeaders(token))
    return res.data
}

export async function setCmPartnerTier(token: string, id: string, tier: CmPartnerTier): Promise<{ tier: CmPartnerTier; tierSource: 'CM'; suggestedTier: CmBookRow['suggestedTier'] }> {
    const res = await cm.patch(`/api/cm/book/partners/${id}/tier`, { tier }, authHeaders(token))
    return res.data
}

export async function getCmYourBook(token: string, params: { q?: string; filter?: CmBookFilter } = {}): Promise<CmYourBook> {
    const res = await cm.get('/api/cm/book', { ...authHeaders(token), params })
    return res.data
}

/** True for the server's depth-book cap refusal. */
export function isDepthCap(e: unknown): boolean {
    const res = (e as { response?: { status?: number; data?: { code?: string } } }).response
    return res?.status === 409 && res.data?.code === 'DEPTH_CAP'
}

// ============================================
// Deals
// ============================================

export async function listCmDeals(
    token: string,
    filters: { stage?: CmDealStage; health?: string; timeframe?: string } = {}
): Promise<{ deals: CmDeal[] }> {
    const res = await cm.get('/api/cm/deals', { ...authHeaders(token), params: filters })
    return res.data
}

export async function getCmDeal(token: string, id: string): Promise<{ deal: CmDeal }> {
    const res = await cm.get(`/api/cm/deals/${id}`, authHeaders(token))
    return res.data
}

export interface CreateCmDealRequest {
    name: string
    stage?: CmDealStage
    mrr: number
    closeDate?: string
    competitor?: string
    committed?: boolean
    isUpside?: boolean
    advisorInitiated?: boolean
    partnerIds?: string[]
}

export async function createCmDeal(token: string, data: CreateCmDealRequest): Promise<{ deal: CmDeal }> {
    const res = await cm.post('/api/cm/deals', data, authHeaders(token))
    return res.data
}

export interface UpdateCmDealRequest extends Partial<CreateCmDealRequest> {
    lostReason?: CmLostReason
    lostReasonDetail?: string
    pushReason?: string
}

export async function updateCmDeal(token: string, id: string, patch: UpdateCmDealRequest): Promise<{ deal: CmDeal }> {
    const res = await cm.patch(`/api/cm/deals/${id}`, patch, authHeaders(token))
    return res.data
}

export async function deleteCmDeal(token: string, id: string) {
    const res = await cm.delete(`/api/cm/deals/${id}`, authHeaders(token))
    return res.data
}

export async function addDealPartner(token: string, dealId: string, partnerId: string): Promise<{ deal: CmDeal }> {
    const res = await cm.post(`/api/cm/deals/${dealId}/partners`, { partnerId }, authHeaders(token))
    return res.data
}

export async function removeDealPartner(token: string, dealId: string, partnerId: string): Promise<{ deal: CmDeal }> {
    const res = await cm.delete(`/api/cm/deals/${dealId}/partners/${partnerId}`, authHeaders(token))
    return res.data
}

// ============================================
// Activities
// ============================================

export async function listCmActivities(
    token: string,
    filters: { partnerId?: string; dealId?: string; tsdContactId?: string; limit?: number } = {}
): Promise<{ activities: CmActivity[] }> {
    const res = await cm.get('/api/cm/activities', { ...authHeaders(token), params: filters })
    return res.data
}

export interface CreateCmActivityRequest {
    type: CmActivityType
    partnerId?: string
    dealId?: string
    tsdContactId?: string
    meetingType?: CmMeetingType
    meetingNature?: CmMeetingNature
    sentiment?: CmSentiment
    notes?: string
    inbound?: boolean
    occurredAt?: string
}

export async function createCmActivity(token: string, data: CreateCmActivityRequest): Promise<{ activity: CmActivity }> {
    const res = await cm.post('/api/cm/activities', data, authHeaders(token))
    return res.data
}

export async function updateCmActivity(token: string, id: string, patch: Partial<CmActivity>): Promise<{ activity: CmActivity }> {
    const res = await cm.patch(`/api/cm/activities/${id}`, patch, authHeaders(token))
    return res.data
}

export async function deleteCmActivity(token: string, id: string) {
    const res = await cm.delete(`/api/cm/activities/${id}`, authHeaders(token))
    return res.data
}

// ============================================
// TSDs
// ============================================

export async function listCmTsdCompanies(token: string): Promise<{ companies: CmTsdCompany[] }> {
    const res = await cm.get('/api/cm/tsd-companies', authHeaders(token))
    return res.data
}

export async function getCmTsdCompany(token: string, id: string): Promise<{ company: CmTsdCompany }> {
    const res = await cm.get(`/api/cm/tsd-companies/${id}`, authHeaders(token))
    return res.data
}

export async function createCmTsdCompany(token: string, data: { name: string; quarterlyIntroTarget?: number }) {
    const res = await cm.post('/api/cm/tsd-companies', data, authHeaders(token))
    return res.data
}

export async function updateCmTsdCompany(token: string, id: string, patch: Partial<CmTsdCompany>) {
    const res = await cm.patch(`/api/cm/tsd-companies/${id}`, patch, authHeaders(token))
    return res.data
}

export async function listCmTsdContacts(token: string, tsdCompanyId?: string): Promise<{ contacts: CmTsdContact[] }> {
    const params = tsdCompanyId ? { tsdCompanyId } : {}
    const res = await cm.get('/api/cm/tsd-contacts', { ...authHeaders(token), params })
    return res.data
}

export async function createCmTsdContact(token: string, data: any) {
    const res = await cm.post('/api/cm/tsd-contacts', data, authHeaders(token))
    return res.data
}

export async function updateCmTsdContact(token: string, id: string, patch: Partial<CmTsdContact>) {
    const res = await cm.patch(`/api/cm/tsd-contacts/${id}`, patch, authHeaders(token))
    return res.data
}

export async function getCmTsdContact(token: string, id: string): Promise<{ contact: CmTsdContact }> {
    const res = await cm.get(`/api/cm/tsd-contacts/${id}`, authHeaders(token))
    return res.data
}

export async function deleteCmTsdCompany(token: string, id: string) {
    const res = await cm.delete(`/api/cm/tsd-companies/${id}`, authHeaders(token))
    return res.data
}

export async function deleteCmTsdContact(token: string, id: string) {
    const res = await cm.delete(`/api/cm/tsd-contacts/${id}`, authHeaders(token))
    return res.data
}

// ============================================
// Playbooks
// ============================================

export interface PlaybookTemplate {
    key: string
    name: string
    priority: CmPriority
    description: string
    stepCount: number
    durationDays: number
}

export async function listPlaybookTemplates(token: string): Promise<{ templates: PlaybookTemplate[] }> {
    const res = await cm.get('/api/cm/playbook-templates', authHeaders(token))
    return res.data
}

export async function listCmPlaybooks(token: string): Promise<{ playbooks: CmPlaybook[] }> {
    const res = await cm.get('/api/cm/playbooks', authHeaders(token))
    return res.data
}

export async function getCmPlaybook(token: string, id: string): Promise<{ playbook: CmPlaybook }> {
    const res = await cm.get(`/api/cm/playbooks/${id}`, authHeaders(token))
    return res.data
}

export async function createPlaybookFromTemplate(
    token: string,
    data: { templateKey: string; partnerId?: string; name?: string; priority?: CmPriority }
) {
    const res = await cm.post('/api/cm/playbooks/from-template', data, authHeaders(token))
    return res.data
}

export async function createCustomPlaybook(token: string, data: any) {
    const res = await cm.post('/api/cm/playbooks/custom', data, authHeaders(token))
    return res.data
}

export interface AiPlaybookMessage {
    role: 'user' | 'assistant'
    content: string
}

export interface AiPlaybookDraft {
    name: string
    priority: CmPriority
    steps: Array<{ day: number; phase: string | null; title: string; description: string | null }>
}

export interface AiPlaybookChatResponse {
    reply: string
    askingFor: string | null
    draftPlaybook: AiPlaybookDraft | null
    error?: string
}

export async function aiPlaybookChat(
    token: string,
    data: { partnerId?: string; history: AiPlaybookMessage[] }
): Promise<AiPlaybookChatResponse> {
    const res = await cm.post('/api/cm/playbooks/ai/chat', data, authHeaders(token))
    return res.data
}

export async function updatePlaybookStep(token: string, id: string, status: CmStepStatus) {
    const res = await cm.patch(`/api/cm/playbook-steps/${id}`, { status }, authHeaders(token))
    return res.data
}

export async function updatePlaybookStepContent(token: string, id: string, data: { title?: string; description?: string }) {
    const res = await cm.patch(`/api/cm/playbook-steps/${id}`, data, authHeaders(token))
    return res.data
}

export async function updateCmPlaybook(token: string, id: string, data: { outcomeNotes?: string | null }) {
    const res = await cm.patch(`/api/cm/playbooks/${id}`, data, authHeaders(token))
    return res.data
}

export async function deleteCmPlaybook(token: string, id: string) {
    const res = await cm.delete(`/api/cm/playbooks/${id}`, authHeaders(token))
    return res.data
}

// ============================================
// Contact Groups
// ============================================

export async function listCmGroups(token: string): Promise<{ groups: CmContactGroup[] }> {
    const res = await cm.get('/api/cm/groups', authHeaders(token))
    return res.data
}

export async function getCmGroup(token: string, id: string): Promise<{ group: CmContactGroup }> {
    const res = await cm.get(`/api/cm/groups/${id}`, authHeaders(token))
    return res.data
}

export async function createCmGroup(token: string, data: { name: string; cadenceDays?: number; autoActions?: boolean; partnerIds?: string[] }) {
    const res = await cm.post('/api/cm/groups', data, authHeaders(token))
    return res.data
}

export async function createCmGroupFromCsv(token: string, data: { name: string; csv: string; cadenceDays?: number; autoActions?: boolean }) {
    const res = await cm.post('/api/cm/groups/csv', data, authHeaders(token))
    return res.data
}

export async function updateCmGroup(token: string, id: string, patch: Partial<CmContactGroup>) {
    const res = await cm.patch(`/api/cm/groups/${id}`, patch, authHeaders(token))
    return res.data
}

export async function addCmGroupMembers(token: string, id: string, partnerIds: string[]) {
    const res = await cm.post(`/api/cm/groups/${id}/members`, { partnerIds }, authHeaders(token))
    return res.data
}

export async function removeCmGroupMember(token: string, id: string, partnerId: string) {
    const res = await cm.delete(`/api/cm/groups/${id}/members/${partnerId}`, authHeaders(token))
    return res.data
}

export async function deleteCmGroup(token: string, id: string) {
    const res = await cm.delete(`/api/cm/groups/${id}`, authHeaders(token))
    return res.data
}

export async function runGroupPlaybook(token: string, groupId: string, templateKey: string): Promise<{ created: number; total: number }> {
    const res = await cm.post(`/api/cm/groups/${groupId}/run-playbook`, { templateKey }, authHeaders(token))
    return res.data
}

// ============================================
// Today's Actions + KPIs + Intelligence
// ============================================

export async function getTodaysActions(token: string): Promise<CmTodaysActions> {
    const res = await cm.get('/api/cm/todays-actions', authHeaders(token))
    return res.data
}

export async function completeAction(token: string, actionKey: string) {
    const res = await cm.post(`/api/cm/todays-actions/${actionKey}/complete`, {}, authHeaders(token))
    return res.data
}

export async function getCmNotificationCount(token: string) {
    const res = await cm.get('/api/cm/notifications/count', authHeaders(token))
    return res.data
}

export async function getCmKpis(token: string): Promise<CmKpis> {
    const res = await cm.get('/api/cm/kpis', authHeaders(token))
    return res.data
}

export async function getCmDiagnostics(token: string): Promise<{ diagnostics: CmDiagnosticRow[] }> {
    const res = await cm.get('/api/cm/intelligence/diagnostics', authHeaders(token))
    return res.data
}

export interface CmSignalsResponse {
    signals: CmSignal[]
    totals: { growth: number; churn: number; stall: number; intel?: number }
    capped: { growth: boolean; churn: boolean; stall: boolean; intel?: boolean }
}

export async function getCmSignals(token: string): Promise<CmSignalsResponse> {
    const res = await cm.get('/api/cm/intelligence/signals', authHeaders(token))
    return res.data
}

// ---- Partner quick view (G1.3)

export async function getCmPartnerPeek(token: string, id: string): Promise<CmPartnerPeek> {
    const res = await cm.get(`/api/cm/book/partners/${id}/peek`, authHeaders(token))
    return res.data
}

export async function addCmNote(token: string, partnerId: string, text: string): Promise<{ entry: CmLogEntry }> {
    const res = await cm.post(`/api/cm/book/partners/${partnerId}/notes`, { text }, authHeaders(token))
    return res.data
}

export async function removeCmNote(token: string, noteId: string): Promise<void> {
    await cm.delete(`/api/cm/book/notes/${noteId}`, authHeaders(token))
}

export async function addCmTouch(
    token: string, partnerId: string, touch: { kind: CmTouchKind; text: string; isValueTouch: boolean; carriedAsk: boolean }
): Promise<{ entry: CmLogEntry; previousLastContactAt: string | null }> {
    const res = await cm.post(`/api/cm/book/partners/${partnerId}/touches`, touch, authHeaders(token))
    return res.data
}

export async function undoCmTouch(token: string, activityId: string, previousLastContactAt: string | null): Promise<void> {
    await cm.post(`/api/cm/book/touches/${activityId}/undo`, { previousLastContactAt }, authHeaders(token))
}

export async function setCmPromiseStatus(token: string, id: string, status: 'OPEN' | 'DONE'): Promise<void> {
    await cm.patch(`/api/cm/book/promises/${id}`, { status }, authHeaders(token))
}

// ---- Add / edit a partner by hand (the quick view's form, G1.3)

export interface CmPartnerForm {
    name: string
    company: string
    email: string
    phone: string
    city: string
    state: string
    preferredContact: 'CALL' | 'TEXT' | 'EMAIL' | null
}

/** Same email, or same name + firm, is a 409 with a sentence to show. */
export async function addCmBookPartner(token: string, form: CmPartnerForm): Promise<{ id: string }> {
    const res = await cm.post('/api/cm/book/partners', form, authHeaders(token))
    return res.data
}

export async function updateCmBookPartner(token: string, id: string, form: CmPartnerForm): Promise<{ id: string }> {
    const res = await cm.put(`/api/cm/book/partners/${id}`, form, authHeaders(token))
    return res.data
}
